import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShareNetwork, FloppyDisk, GridFour, MagnetStraight, CaretDown,
  Export, FrameCorners, ChatText, Stack, FilmSlate, Question,
  ArrowCounterClockwise, ArrowClockwise,
  Eye, ImageSquare, GearSix, Sun, Moon,
  Chat, Sliders,
} from '@phosphor-icons/react';
import { api } from '../../lib/api-client';
import { getSpineManager } from '../../lib/spine-manager';
import { useSceneStore } from '../../stores/sceneStore';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useToolStore } from '../../stores/toolStore';
import { useOnionStore } from '../../stores/onionStore';
import { useThemeStore } from '../../stores/themeStore';
import { useSubmissionStore } from '../../stores/submissionStore';
import { useReviewStore } from '../../stores/reviewStore';
import { useDragDrop } from '../../hooks/useDragDrop';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useAutoSave } from '../../hooks/useAutoSave';
import EditorLayout from './EditorLayout';
import SceneCanvas from '../canvas/SceneCanvas';
import CanvasLayerHUD from '../canvas/CanvasLayerHUD';
import ContextTip from '../canvas/ContextTip';
import LeftPanel from '../panels/LeftPanel';
import InspectorPanel from '../panels/InspectorPanel';
import ReviewFeedbackPanel from '../review/ReviewFeedbackPanel';
import ShareDialog from '../shared/ShareDialog';
import DragDropImportOverlay from '../shared/DragDropImportOverlay';
import UserMenu from '../shared/UserMenu';
import ExportAnimDialog from './ExportAnimDialog';
import ShortcutsDialog from './ShortcutsDialog';
import ScenePropertiesDialog from './ScenePropertiesDialog';
import { Badge } from '../ui/badge';
import type { Scene } from '@spine-workspace/shared';

// ─── Right sidebar tab switcher ───────────────────────────────────────────────

interface RightSidebarContentProps {
  submissionId: string | null;
  sceneId?: string;
  viewOnly: boolean;
  reviewOpen: boolean;
  onSelectInspector: () => void;
  onSelectReview: () => void;
  commentCount: number;
}

function RightSidebarContent({
  submissionId,
  sceneId,
  viewOnly,
  reviewOpen,
  onSelectInspector,
  onSelectReview,
  commentCount,
}: RightSidebarContentProps) {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-stretch shrink-0 border-b border-border-subtle bg-bg-surface">
        <button
          onClick={onSelectInspector}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[9px] font-mono uppercase tracking-widest transition-colors duration-fast border-b-2 ${
            !reviewOpen
              ? 'text-accent border-accent'
              : 'text-text-tertiary border-transparent hover:text-text-secondary'
          }`}
        >
          <Sliders size={10} />
          Inspector
        </button>
        <button
          onClick={onSelectReview}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[9px] font-mono uppercase tracking-widest transition-colors duration-fast border-b-2 ${
            reviewOpen
              ? 'text-accent border-accent'
              : 'text-text-tertiary border-transparent hover:text-text-secondary'
          }`}
        >
          <Chat size={10} />
          Review
          {commentCount > 0 && (
            <span
              className={`min-w-[14px] h-3.5 px-0.5 rounded-full text-[7px] font-mono flex items-center justify-center ${
                reviewOpen ? 'bg-accent text-white' : 'bg-accent/20 text-accent'
              }`}
            >
              {Math.min(commentCount, 99)}
            </span>
          )}
        </button>
      </div>

      {/* Active tab content */}
      <div className="flex-1 overflow-hidden">
        {reviewOpen ? (
          <ReviewFeedbackPanel submissionId={submissionId} sceneId={sceneId} />
        ) : (
          <InspectorPanel viewOnly={viewOnly} />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const CANVAS_PRESETS = [
  { label: '1920 × 1080 (Full HD)', w: 1920, h: 1080 },
  { label: '1280 × 720 (HD)',       w: 1280, h: 720  },
  { label: '2560 × 1440 (2K)',      w: 2560, h: 1440 },
  { label: '3840 × 2160 (4K)',      w: 3840, h: 2160 },
];

export default function EditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [viewOnly, setViewOnly] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showExportAnim, setShowExportAnim] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSceneProps, setShowSceneProps] = useState(false);
  const [canvasPickerOpen, setCanvasPickerOpen] = useState(false);
  const [safeZoneOpen, setSafeZoneOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [reviewSubmissionId, setReviewSubmissionId] = useState<string | null>(null);

  const {
    initScene, layers, isDirty, save, undo, redo, undoStack, redoStack,
    sceneId, sceneName, canvasWidth, canvasHeight, setCanvasSize, setSceneName,
    backgroundColor, setBackgroundColor, fetchAnnotations,
  } = useSceneStore();
  const { play: playbackPlay } = usePlaybackStore();
  const { gridVisible, snapEnabled, toggleGrid, toggleSnap, safeZoneAspect, setSafeZone, activeTool, setTool, isPresentationMode, togglePresentationMode } = useToolStore();
  const { theme, toggle: toggleTheme } = useThemeStore();
  const { enabled: onionEnabled, toggle: toggleOnion } = useOnionStore();
  const { handleDropOnCanvas, addAssetAtCenter } = useDragDrop();
  const { isOpen: reviewOpen, toggle: toggleReview, open: openReview } = useReviewStore();
  const reviewCommentCount = useSubmissionStore(
    (s) => (reviewSubmissionId ? (s.comments[reviewSubmissionId]?.length ?? 0) : 0)
  );

  const canvasPickerRef = useRef<HTMLDivElement>(null);
  const safeZoneRef = useRef<HTMLDivElement>(null);
  const viewMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useKeyboardShortcuts(
    () => getSpineManager().fitToArtboard(canvasWidth, canvasHeight),
    () => setShowShortcuts((s) => !s),
  );
  useAutoSave();

  // Close dropdowns on outside click
  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (canvasPickerRef.current && !canvasPickerRef.current.contains(e.target as Node)) setCanvasPickerOpen(false);
      if (safeZoneRef.current && !safeZoneRef.current.contains(e.target as Node)) setSafeZoneOpen(false);
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target as Node)) setViewMenuOpen(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) setExportMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get<{ scene: Scene }>(`/api/scenes/${id}`)
      .then(({ scene }) => {
        const isViewOnly = scene.permission === 'view';
        setViewOnly(isViewOnly);
        setReviewSubmissionId((scene as Scene & { reviewSubmissionId?: string }).reviewSubmissionId ?? null);
        initScene(
          scene.id, scene.name, scene.canvasWidth, scene.canvasHeight,
          scene.sceneData?.layers ?? [], scene.sceneData?.canvas?.backgroundColor ?? null,
          scene.sceneData?.layerGroups ?? [], scene.sceneData?.notes ?? ''
        );
        if (usePlaybackStore.getState().autoPlayOnLoad) playbackPlay();
        setTimeout(() => fetchAnnotations(), 0);
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [id, navigate, initScene, fetchAnnotations]);

  // Auto-open review panel when scene is a review canvas
  useEffect(() => {
    if (reviewSubmissionId) openReview();
  }, [reviewSubmissionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = useCallback((assetData: string, x: number, y: number) => {
    handleDropOnCanvas(assetData, x, y);
  }, [handleDropOnCanvas]);

  async function exportFramePng() {
    try {
      const blob = await getSpineManager().captureScreenshot();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${sceneName.replace(/\s+/g, '_')}_frame.png`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Frame export failed', e); }
  }

  function exportSceneJson() {
    const { layers, canvasWidth, canvasHeight, sceneName } = useSceneStore.getState();
    const exportData = {
      schemaVersion: '1.0',
      canvas: { width: canvasWidth, height: canvasHeight },
      layers: layers.map((l) => ({
        assetName: l.name, assetId: l.assetId,
        position: { x: l.transform.x, y: -l.transform.y },
        scale: { x: l.transform.scaleX, y: l.transform.scaleY },
        rotation: -l.transform.rotation,
        skin: l.skin, animation: l.animation.tracks[0]?.name ?? null,
        visible: l.visible, opacity: l.opacity,
      })),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${sceneName.replace(/\s+/g, '_')}.scene.json`; a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-deepest text-text-tertiary text-sm font-sans">
        Loading scene…
      </div>
    );
  }

  // True when any view option is active — shows dot on View button
  const anyViewActive = gridVisible || snapEnabled || onionEnabled || safeZoneAspect !== null || activeTool === 'annotate' || backgroundColor !== null;

  const header = (
    <div className="h-[52px] bg-bg-surface border-b border-border-subtle px-4 flex items-center gap-0">

      {/* ── LEFT: Logo + scene name ── */}
      <div className="flex items-center gap-2.5 shrink-0">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 group"
          title="Back to dashboard"
        >
          <div className="w-7 h-7 rounded-md bg-accent flex items-center justify-center shrink-0">
            <ArrowLeft size={14} weight="bold" className="text-white group-hover:opacity-80 transition-opacity" />
          </div>
          <span className="font-serif text-base font-medium text-text-primary hidden sm:block leading-none">
            Spine Workspace
          </span>
        </button>

        <div className="w-px h-5 bg-border mx-2 shrink-0" />

        {/* Scene name + status */}
        <div className="flex items-center gap-1.5 min-w-0">
          {editingName ? (
            <input
              autoFocus
              value={nameDraft}
              data-testid="scene-name-input"
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => { const n = nameDraft.trim(); if (n) setSceneName(n); setEditingName(false); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { const n = nameDraft.trim(); if (n) setSceneName(n); setEditingName(false); }
                if (e.key === 'Escape') setEditingName(false);
                e.stopPropagation();
              }}
              className="font-sans text-sm text-text-primary font-medium bg-bg-input border border-accent rounded px-1.5 py-0.5 outline-none hidden sm:block max-w-[160px]"
            />
          ) : (
            <span
              data-testid="scene-name"
              onClick={() => { if (!viewOnly) { setNameDraft(sceneName); setEditingName(true); } }}
              title={viewOnly ? sceneName : 'Click to rename'}
              className={`font-sans text-sm text-text-primary font-medium truncate hidden sm:block max-w-[160px] ${!viewOnly ? 'cursor-text hover:text-accent transition-colors duration-fast' : ''}`}
            >
              {sceneName}
            </span>
          )}
          {/* Cog — scene properties, owner/editor only */}
          {!viewOnly && !editingName && (
            <button
              data-testid="scene-props-btn"
              onClick={() => setShowSceneProps(true)}
              title="Scene properties"
              className="w-[22px] h-[22px] flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-all duration-fast shrink-0"
            >
              <GearSix size={12} />
            </button>
          )}
          {viewOnly
            ? <Badge variant="warning">View only</Badge>
            : isDirty ? <Badge variant="warning">Unsaved</Badge> : <Badge variant="success">Saved</Badge>
          }
        </div>
      </div>

      {/* ── CENTER: Canvas size + Zoom + Undo/Redo ── */}
      <div className="flex-1 flex items-center justify-center gap-1">

        {/* Canvas size picker */}
        <div className="relative hidden lg:block" ref={canvasPickerRef}>
          <button
            onClick={() => setCanvasPickerOpen((o) => !o)}
            className="flex items-center gap-1 font-mono text-xs text-text-secondary bg-bg-elevated border border-border px-2 py-[4px] rounded-sm hover:border-border-focus hover:text-text-primary transition-all duration-fast"
          >
            {canvasWidth} × {canvasHeight}
            <CaretDown size={9} className={`transition-transform duration-fast ${canvasPickerOpen ? 'rotate-180' : ''}`} />
          </button>
          {canvasPickerOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-bg-elevated border border-border rounded-md shadow-lg py-1 min-w-[200px]">
              {CANVAS_PRESETS.map((p) => (
                <button
                  key={`${p.w}x${p.h}`}
                  onClick={() => { setCanvasSize(p.w, p.h); setCanvasPickerOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs font-sans transition-colors duration-fast ${
                    canvasWidth === p.w && canvasHeight === p.h
                      ? 'text-accent bg-accent-muted' : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-4 bg-border hidden lg:block mx-1" />

        {/* Safe Zone dropdown */}
        <div className="relative hidden lg:block" ref={safeZoneRef}>
          <button
            data-testid="safe-zone-btn"
            onClick={() => setSafeZoneOpen((o) => !o)}
            className={`flex items-center gap-1 font-mono text-xs px-2 py-[4px] rounded-sm border transition-all duration-fast ${
              safeZoneAspect
                ? 'bg-accent-muted text-accent border-accent/30'
                : 'text-text-secondary bg-bg-elevated border-border hover:border-border-focus hover:text-text-primary'
            }`}
          >
            <FrameCorners size={11} />
            {safeZoneAspect ?? 'Safe Zone'}
            <CaretDown size={9} className={`transition-transform duration-fast ${safeZoneOpen ? 'rotate-180' : ''}`} />
          </button>
          {safeZoneOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-bg-elevated border border-border rounded-md shadow-lg py-1 min-w-[100px]">
              <button
                onClick={() => { setSafeZone(null); setSafeZoneOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors duration-fast ${
                  safeZoneAspect === null ? 'text-accent bg-accent-muted' : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >None</button>
              {(['16:9', '9:16', '4:3', '1:1'] as const).map((ratio) => (
                <button
                  key={ratio}
                  data-testid={`safe-zone-${ratio.replace(':', 'x')}`}
                  onClick={() => { setSafeZone(ratio); setSafeZoneOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs font-mono transition-colors duration-fast ${
                    safeZoneAspect === ratio ? 'text-accent bg-accent-muted' : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >{ratio}</button>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-4 bg-border hidden lg:block mx-1" />

        {/* Undo / Redo */}
        <div className="hidden lg:flex items-center gap-0.5">
          <button
            data-testid="undo-btn"
            onClick={undo}
            disabled={undoStack.length === 0}
            className="w-[26px] h-[26px] flex items-center justify-center rounded text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast disabled:opacity-35 disabled:cursor-default"
            title="Undo (Ctrl+Z)"
          >
            <ArrowCounterClockwise size={13} />
          </button>
          <button
            data-testid="redo-btn"
            onClick={redo}
            disabled={redoStack.length === 0}
            className="w-[26px] h-[26px] flex items-center justify-center rounded text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast disabled:opacity-35 disabled:cursor-default"
            title="Redo (Ctrl+Y)"
          >
            <ArrowClockwise size={13} />
          </button>
        </div>
      </div>

      {/* ── RIGHT: View ▾ | Export ▾ | Save | Share | ? | Avatar ── */}
      <div className="flex items-center gap-1.5 shrink-0">

        {/* View dropdown */}
        <div className="relative hidden lg:block" ref={viewMenuRef}>
          <button
            data-testid="view-menu-btn"
            onClick={() => { setViewMenuOpen((o) => !o); setExportMenuOpen(false); }}
            className={`flex items-center gap-1.5 px-2.5 py-[5px] rounded-md text-xs font-sans transition-all duration-fast ${
              viewMenuOpen
                ? 'bg-bg-hover text-text-primary'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            <Eye size={13} />
            <span>View</span>
            {anyViewActive && <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />}
            <CaretDown size={9} className={`transition-transform duration-fast ${viewMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {viewMenuOpen && (
            <div className="absolute top-full right-0 mt-1 z-50 bg-bg-elevated border border-border rounded-lg shadow-xl py-1.5 w-[220px]">

              {/* BG Color */}
              <div className="px-3 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-text-tertiary mb-1.5">Background</p>
                <div className="flex items-center gap-1.5">
                  <button
                    title="Transparent"
                    onClick={() => setBackgroundColor(null)}
                    className={`flex items-center justify-center w-6 h-6 rounded border transition-all duration-fast ${
                      backgroundColor === null ? 'border-accent ring-1 ring-accent/40' : 'border-border hover:border-border-hover'
                    }`}
                    style={{ background: 'repeating-conic-gradient(#555 0% 25%, #333 0% 50%) 0 0 / 6px 6px' }}
                  />
                  <label className="flex items-center gap-1.5 px-2 py-1 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded cursor-pointer transition-all duration-fast flex-1">
                    <span className="w-4 h-4 rounded border border-border shrink-0" style={{ backgroundColor: backgroundColor ?? '#000000' }} />
                    <span>{backgroundColor ? backgroundColor.toUpperCase() : 'Custom color'}</span>
                    <input type="color" value={backgroundColor ?? '#000000'} onChange={(e) => setBackgroundColor(e.target.value)} className="sr-only" />
                  </label>
                </div>
              </div>

              <div className="border-t border-border-subtle my-1" />

              {/* Toggle items */}
              {[
                { icon: <GridFour size={13} />, label: 'Grid', active: gridVisible, onClick: toggleGrid },
                { icon: <MagnetStraight size={13} />, label: 'Snap to grid', active: snapEnabled, onClick: toggleSnap },
                { icon: <Stack size={13} />, label: 'Onion skinning', active: onionEnabled, onClick: toggleOnion, testid: 'onion-skin-btn' },
                { icon: <ChatText size={13} />, label: 'Annotate mode', active: activeTool === 'annotate', onClick: () => setTool(activeTool === 'annotate' ? 'select' : 'annotate'), testid: 'annotate-mode-btn' },
              ].map(({ icon, label, active, onClick, testid }) => (
                <button
                  key={label}
                  data-testid={testid}
                  onClick={onClick}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans transition-colors duration-fast ${
                    active ? 'text-accent' : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  {icon}
                  <span className="flex-1 text-left">{label}</span>
                  {active && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
                </button>
              ))}

              <div className="border-t border-border-subtle my-1" />

              {/* Safe zone */}
              <div className="px-3 py-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-text-tertiary mb-1.5">Safe zone</p>
                <div className="flex items-center gap-1">
                  {(['16:9', '9:16', '4:3', '1:1'] as const).map((ratio) => (
                    <button
                      key={ratio}
                      data-testid={`safe-zone-${ratio.replace(':', 'x')}`}
                      onClick={() => setSafeZone(safeZoneAspect === ratio ? null : ratio)}
                      className={`flex-1 py-[4px] rounded text-[10px] font-mono transition-all duration-fast ${
                        safeZoneAspect === ratio
                          ? 'bg-accent-muted text-accent border border-accent/30'
                          : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover border border-transparent'
                      }`}
                    >
                      {ratio}
                    </button>
                  ))}
                  <FrameCorners size={11} className="text-text-tertiary ml-0.5 shrink-0" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Export dropdown */}
        <div className="relative hidden lg:block" ref={exportMenuRef}>
          <button
            data-testid="export-menu-btn"
            onClick={() => { setExportMenuOpen((o) => !o); setViewMenuOpen(false); }}
            className={`flex items-center gap-1.5 px-2.5 py-[5px] rounded-md text-xs font-sans transition-all duration-fast ${
              exportMenuOpen
                ? 'bg-bg-hover text-text-primary'
                : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            <Export size={13} />
            <span>Export</span>
            <CaretDown size={9} className={`transition-transform duration-fast ${exportMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {exportMenuOpen && (
            <div className="absolute top-full right-0 mt-1 z-50 bg-bg-elevated border border-border rounded-lg shadow-xl py-1.5 w-[200px]">
              <button
                data-testid="export-frame-btn"
                onClick={() => { exportFramePng(); setExportMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
              >
                <ImageSquare size={13} />
                <span>Frame as PNG</span>
              </button>
              <button
                data-testid="export-anim-open-btn"
                onClick={() => { setShowExportAnim(true); setExportMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
              >
                <FilmSlate size={13} />
                <span>Animation (GIF / WebM)</span>
              </button>
              <div className="border-t border-border-subtle my-1" />
              <button
                data-testid="export-scene-btn"
                onClick={() => { exportSceneJson(); setExportMenuOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
              >
                <Export size={13} />
                <span>Scene JSON (Unity)</span>
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-border mx-0.5 hidden lg:block" />

        {/* Review panel toggle */}
        <button
          data-testid="review-panel-btn"
          onClick={toggleReview}
          title="Review feedback (R)"
          className={`flex items-center gap-1.5 px-2.5 py-[5px] text-xs font-sans font-medium rounded-md border transition-all duration-normal ${
            reviewOpen
              ? 'bg-accent-muted text-accent border-accent/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover border-border'
          }`}
        >
          <Chat size={13} />
          <span className="hidden sm:block">Review</span>
          {reviewCommentCount > 0 && (
            <span className={`min-w-[16px] h-4 px-1 rounded-full text-[8px] font-mono flex items-center justify-center ${
              reviewOpen ? 'bg-accent text-white' : 'bg-accent/20 text-accent'
            }`}>
              {Math.min(reviewCommentCount, 99)}
            </span>
          )}
        </button>

        {/* Save — hidden for viewers */}
        {!viewOnly && (
          <button
            onClick={() => save()}
            disabled={!isDirty}
            className={`flex items-center gap-1.5 px-3 py-[5px] text-xs font-sans font-medium rounded-md transition-all duration-normal ${
              isDirty
                ? 'bg-bg-input border border-border text-text-primary hover:bg-bg-hover'
                : 'text-text-tertiary cursor-default'
            }`}
          >
            <FloppyDisk size={13} />
            <span className="hidden sm:block">{isDirty ? 'Save' : 'Saved'}</span>
          </button>
        )}

        {/* Share — hidden for viewers */}
        {!viewOnly && (
          <button
            onClick={() => setShowShare(true)}
            className="flex items-center gap-1.5 px-3 py-[5px] text-xs font-sans font-medium bg-accent text-white rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
          >
            <ShareNetwork size={13} />
            <span className="hidden sm:block">Share</span>
          </button>
        )}

        {/* Presentation mode */}
        <button
          data-testid="presentation-mode-btn"
          onClick={togglePresentationMode}
          title="Presentation mode (F5)"
          className={`w-[30px] h-[30px] flex items-center justify-center rounded-md border transition-all duration-normal ${
            isPresentationMode
              ? 'text-accent bg-accent-muted border-accent/30'
              : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover border-border'
          }`}
        >
          <FrameCorners size={13} />
        </button>

        {/* Shortcuts */}
        <button
          data-testid="shortcuts-btn"
          onClick={() => setShowShortcuts(true)}
          title="Keyboard shortcuts (?)"
          className="w-[30px] h-[30px] flex items-center justify-center rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-hover border border-border transition-all duration-normal"
        >
          <Question size={13} />
        </button>

        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="w-[30px] h-[30px] flex items-center justify-center rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-hover border border-border transition-all duration-normal"
        >
          {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
        </button>

        <UserMenu size={30} />
      </div>
    </div>
  );

  return (
    <>
      {sceneId && <ShareDialog open={showShare} onClose={() => setShowShare(false)} sceneId={sceneId} />}
      <ExportAnimDialog open={showExportAnim} onClose={() => setShowExportAnim(false)} />
      <ShortcutsDialog open={showShortcuts} onClose={() => setShowShortcuts(false)} />
      <ScenePropertiesDialog open={showSceneProps} onClose={() => setShowSceneProps(false)} />
      {!viewOnly && <DragDropImportOverlay />}
      <EditorLayout
        header={header}
        leftSidebar={<LeftPanel onDoubleClickAsset={viewOnly ? undefined : addAssetAtCenter} viewOnly={viewOnly} />}
        canvas={
          <div className="w-full h-full relative">
            <SceneCanvas canvasWidth={canvasWidth} canvasHeight={canvasHeight} layers={layers} onDropAsset={viewOnly ? undefined : onDrop} readOnly={viewOnly} />
            {!viewOnly && <CanvasLayerHUD />}
            {!viewOnly && <ContextTip />}
          </div>
        }
        rightSidebar={
          <RightSidebarContent
            submissionId={reviewSubmissionId}
            sceneId={sceneId ?? undefined}
            viewOnly={viewOnly}
            reviewOpen={reviewOpen}
            onSelectInspector={() => useReviewStore.getState().close()}
            onSelectReview={() => useReviewStore.getState().open()}
            commentCount={reviewCommentCount}
          />
        }
      />
    </>
  );
}
